import { Box } from '@mui/material'
import { forwardRef } from 'react'
import { Fade } from 'utoniq-ui'

import { styles, styleSets } from '@/components/styles'
import { OrganismFrame, OrganismFrameProps } from '@/core/architecture'
import { useImage } from '@/lib/image'

export type HeaderImageAreaProps = OrganismFrameProps & {
  imageUri?: string | null
  hasSafeArea?: boolean
  bgImage?: string | null
  gradationMask?: boolean
}

export const HeaderImageArea = forwardRef<HTMLDivElement, HeaderImageAreaProps>(
  (
    { imageUri, hasSafeArea, bgImage, gradationMask, children, ...props },
    ref
  ) => {
    const image = useImage(imageUri || '')
    const bannerAspectRatio =
      !!image?.naturalWidth && !!image?.naturalHeight
        ? image.naturalHeight / image.naturalWidth
        : null

    return (
      <OrganismFrame
        {...props}
        sx={{
          height: styleSets.bgImageHeight(bannerAspectRatio),
          maxHeight: 768,
          pt: hasSafeArea ? styles.safeAreaTop : 0,
          position: 'relative',
          zIndex: 0,
          overflow: 'hidden',
          ...props?.sx,
        }}
      >
        {!!imageUri && (
          <Fade in>
            <Box
              ref={ref}
              sx={{
                position: 'absolute',
                left: 0,
                top: 0,
                width: '100%',
                height: '100%',
                zIndex: -1,
                backgroundImage: `url(${imageUri})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                visibility:
                  !!image?.naturalWidth && !!image?.naturalHeight
                    ? 'visible'
                    : 'hidden',
              }}
            />
          </Fade>
        )}
        {gradationMask && (
          <Box
            ref={ref}
            sx={{
              pointerEvents: 'none',
              bgcolor: 'background.default',
              height: '100vh',
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              position: 'absolute',
              zIndex: 1,
              top: 0,
              width: '100%',
              maskImage: () => {
                const gradationStyle = (width: string) =>
                  `linear-gradient(0deg, black calc(100vh - ${width} * ${
                    bannerAspectRatio ?? 0
                  } + 24px), transparent calc(100vh - ${width} * ${
                    bannerAspectRatio ?? 0
                  } + 140px))`

                return {
                  xs: gradationStyle('100vw'),
                  sm: gradationStyle('600px'),
                }
              },
              backgroundImage: bgImage ? `url(${bgImage})` : 'none',
            }}
          />
        )}
        {children}
      </OrganismFrame>
    )
  }
)
